<template>
<div class="chapter">
<div class="inner_page">
<p class="ct">
<a href="#/contents#ch1">CHAPTER I</a></p>
<p class="ct1"><a href="#/contents#ch1">
<i>Moscow Exchange Year, 1961-62</i></a></p>
<p class="tx">
					My wife, Lois, and I arrived in Moscow by train via Paris, Prague, and Warsaw in
					September 1961. As I was in Moscow to write a dissertation on Maxim Gorky’s dramaturgy,
					it should be clear that Orwell’s <i>Homage to Catalonia</i> had not yet cured me of socialist
					romanticism. During a Fulbright year in Hamburg, an émigré Russian woman had indeed
					overcome much of my ignorance. At the same time, her warmth and the bewitching sounds
					of spoken Russian (we traded language lessons) made Russia as attractive as the Soviet
					Union had been to me, an ignorant, nineteen-year-old, self-styled Marxist. When I
					studied at Moscow State University, thanks to the cultural exchange treaty blessed
					by Nikita Khrushchev and Dwight D. Eisenhower in 1959, I was a card-carrying liberal.
					I’d voted for Adlai, gladly. If pushed, I would have admitted that I was in Russia
					to find a life, if not socialist, at least not aspiring to middle class. I gradually
					made out the features of such an existence - that’s the secret thread here - and
					it was <i>not</i> socialist and not middle class either. Life for most Russians had little
					to do with such political and social distinctions at all. Neither socialist asceticism
					nor bourgeois consumption, in our sense, played any significant public role in 1961
					Moscow. </p>
<p class="tx">
					Cultural consumption was another matter. My advisor’s lectures on <span id="z13-1">Gorky and a desk
					at the Lenin Library comparable to Marx’s nest at the British Museum made up my professional
					life. In the evening, though, Madison, Wisconsin, my cultural home until then, paled
					before the lively and various theatrical and concert life of Moscow. In his lectures,
					Professor Vysotsky seemed to imply that Gorky’s dramas, like Lenin’s electricity,
					had guaranteed a Communist future. However, he had a beautiful, pre-revolutionary
					academic accent, Polish “I” (which sounds like “w”). After four years of practice,
					I was just beginning to grasp spoken Russian, and his lectures, though socialistically
					dutiful, went down like Chopin. Then, privately, the professor suggested newer and
					more daring playwrights than Gorky, and arranged a week-long excursion to Leningrad,
					Peter the Great’s “window on the West,” for Lois and me. Once, when I visited him
					in the hospital, suffering from heart trouble, he insisted, like all Russian hosts,
					that I absolutely must share his hospital breakfast. This natural generosity I have
					found throughout the fifty years of my association with Russia and still utterly
					admire. </span></p>
<p class="tx">
					As regards evening activities, Khrushchev’s denunciation of Stalin’s crimes at the
					Twentieth Party Congress in 1956 had transformed, and at least partially liberated,
					the dramatic arts. One could choose Evgeny Shvarts’s <i>The Naked King</i>, a relevant remake
					of “The Emperor’s New Clothes,” or Brecht’s <i>Mother Courage,</i> though Brecht had always
					been suspect in Moscow. From the past, but by no means irrelevant, was all of Chekhov
					at the Moscow Art Theater, albeit in the turn-of-the-century Stanislavsky productions.
					<i>Swan Lake</i> or <i>Boris Godunov</i> in sumptuous settings at the Bolshoy Theater <span id="z14-1">did not preclude
					performances of the more “formalist,” then-recent works of Prokofiev and Shostakovich.
					These might be paired with Haydn, the “Farewell Symphony” with knee britches, wigs
					and candles, or even Corelli and Handel. My grandfather would have called this “God’s
					own plenty,” if one could imagine a nineteenth century lowland Scots teamster in
					1961 Moscow. That is, I was occasionally aware of my impossibly good luck. </span></p>
<p class="tx">
					Our social life was equally varied. We had surprisingly little to do with the dozen
					other Americans studying at the Moscow State University, living in that Stalin “wedding
					cake” on the Vorobyov Hills that overlook Moscow to the north. We had a drink with
					one or another of our cohorts, a formal meeting if group needs were felt, but most
					of us, I think, tried to avoid situations where it would be natural to speak English.
					As two of the couples and a third wife were Scandinavian-born, we were an international
					group anyway. Also, the authorities had settled us in rooms rather far from each
					other in that hideous, comfortable skyscraper, happily mixed with the many other
					foreign students and students from the republics. Moscow Russians, who made up an
					unfairly large segment of the student body because of relatives important in the
					Party, were nevertheless seldom allowed to live at the university because they could
					reasonably commute to classes. </p>
<p class="tx">
					A Muscovite journalism student, Volodya, found us as soon as we arrived. He interviewed
					us for the student newspaper, became our guide and, I think, reported on our conversations
					to the KGB. This was bothersome, seemed false, then sad, and, finally, came not to
					matter at all. We were, after all, not secret agents, and had nothing to reveal but
					our obvious relative <span id="z15-1">prosperity. Volodya took me to observe the municipal elections
					and the children’s ballet that accompanied them at that balloting center. He told
					countless Radio Armenia jokes, which tended to turn upon the supposed homosexuality
					of all Armenians, until we found the civil courage to demur. Volodya also dared to
					show me the one-room communal “apartment” that he shared with his widowed mother.
					I suggest that he was taking a chance because the authorities tried to keep some
					distance between foreigners and Russian citizens. </span></p>
<p class="tx">
					Most of our Russian acquaintances arranged to meet us at quiet places and asked us
					not to dress too “American.” I might have laughed at the simplicity of his one room,
					being from the country where, by definition, the streets are paved with gold. </p>
<p class="tx">
					Volodya’s father, a zealous Communist in good standing, had been arrested in the
					post-WWII purges, and his family learned of his death only in 1959. Still, because
					no career was possible otherwise, Volodya was an ardent, if ironical, member of the
					Komsomol, getting out the vote at election time, neighing and braying properly at
					meetings. He gave me his textbooks on Marxism-Leninism, a required ten percent of
					the graduate curriculum, when I showed interest. He said that <i>he</i> certainly didn’t
					intend to read them. </p>
<p class="tx">
					Another acquaintance that found <i>us</i> was the abstract painter and self-conscious hippy,
					<i>stilyaga</i> in Russian, Andrey. We had been asked by a student from the previous year’s
					group to bring him drawing materials. Andrey, in turn, and his friend Sonia, threw
					a party to welcome us in their downtown apartment. Big band sound, rock, hadn’t arrived
					yet; jeans and sweaters and <span id="z16-1">beards and sandals were not present either. The revolutionary
					talk was not nearly as literate as that of my Hungarian high school friends </span></p>
<p class="tx">
					The walls were covered with Andrey’s then-forbidden, rather nice abstract painting.
					When pushed, I <i>said</i> that the paintings were “rather nice.” I realized too late that
					while this, though perhaps true, because the work was like that of fifty other painters
					lacking a particular <i>self,</i> should not have been said. I immediately became responsible
					for the U2 spy plane which had recently been shot down. Indeed, this was our first
					and last party with Andrey and the other <i>stilyagi</i>, but it did give us, in one evening,
					a quick view of their particular world of dissidence. Andrey had a three-room apartment,
					Levis, a good collection of jazz LPs, and was able to avoid employment, dodge the
					universal draft, and badmouth the regime because his father was an important party
					official. Most of his friends came from families of the <i>nomenklatura</i>, high ranking
					bureaucrats. As in the sixties in America, the revolt of the <i>stilyagi</i> was to a greater
					or lesser degree a generational conflict within the middle class. </p>
<p class="tx">
					At the university, our immediate neighbors and friends were an Uzbek couple, a Tadzhik
					bachelor, and a student of Russian literature from Lahore. These neighbors in the
					corridor, except for the man from Lahore, were much more pious about political matters.
					At least, they didn’t allow themselves jokes at even Stalin’s expense. Svet (“Light”),
					the Uzbek wife, spent long hours queuing to feed and clothe herself and her husband
					and taught us how to insulate our windows with cotton batting when zero weather came
					early. She was utterly grateful to have risen from her father’s shepherd life. <span id="z17-1">The
					Tadzhik bachelor, Yurik, a mathematician, applied himself zealously in his courses
					and, like Svet, rejoiced at the chance to improve upon the fate of his father: a
					village school teacher. Indeed, even in 1961, it was mainly Muscovites who allowed
					themselves subversive “anekdoty,” jokes about the absence of corn production or the
					bad quality of Russian beer, but then Muscovites, like New Yorkers, have traditionally
					sharp and biting tongues. In 1961 this quality was indulged privately. Publicly,
					among those with a career to make or lose, socialist correctness was predominant,
					as is political correctness in the United States. </span></p>
<p class="tx">
					Then there was the man from Lahore. Ari was a fine chef studying to be a professor
					of Russian, who received fresh spices and even special rice by Air India. His cousin
					was a pilot. He alone could occasionally beat Yurik at chess, which was played in
					the corridor lounge at every free moment. I like to think that Lois and I both loved
					him for his anciently correct English and fine manners, as well as for his dinners.
					One evening, Ari was visited by the eleventh-floor Komsomol (Young Communist) committee.
					They insisted upon verifying the cleanliness of his room. He bowed them in. After
					half an hour, unable to fault Ari’s housekeeping, the tallest of the group reached
					up to find a very thin film of dust on the edge of the ceiling fixture. This he pushed
					under the chef’s nose. Ari elegantly grasped the finger between two of his own and
					turned it over. A thick, black moon shone under the giant’s fingernail. The Komsomol
					delegation had the grace to leave without a word. </p>
<p class="tx">
					Speaking generally, one applicant in ten was admitted to Moscow State University
					in 1961. (It must be remembered that Russia follows <span id="z18-1">the European educational system
					in that “university” means graduate school. The place of our “college” is taken by
					the final years of certain elite middle schools.) Perhaps three out of ten graduate
					students were genuine Muscovites. Resident students, often from distant republics,
					were lucky to prepare for careers at this most prestigious of Russian graduate institutions.
					They were aware of their good fortune and worked accordingly. Foreign students like
					us, or Ari, or, say, the African students who, at that time, studied Russian at Lumumba
					University across town before coming to Moscow State, were not in the least impressed.
					The classrooms, laboratories, and physical living conditions, though exceptional
					in Russia, would have been unacceptable in Peoria or Addis Ababa. Some of the African
					students tended to let their disdain be known, and this, along with their assumption
					that socialism meant human equality, even to the point of dating Russian girls, led
					to racial incidents and, more than once, to murder. The western variety of ethnic
					or gender consciousness was literally unheard of in 1961. It had hardly developed,
					even in Moscow, by 1997. Russians, like many provincial peoples, have a surprising
					number of genuine democrats, but the majority has not learned the hypocritical necessity
					of hiding its racism for business or social reasons. Besides, Russian patriotism
					is heavily spiced with inferiority feelings toward a largely unknown, and thus threatening,
					overseas world. This makes racism there particularly vehement. The Iron Curtain played
					a role as well. Most Russians simply could not comprehend the material and social
					expectations of middle-class Africans or Asians. </span></p>
<p class="tx">
					Occasionally, perhaps at the theater, someone would overhear our <span id="z19-1">English and ask,
					“Is it true that you all have cars? Do you own a house? How many square meters of
					floor space?” By 1961, some Moscow Russians had serious doubts about a party line
					that ascribed the world’s highest standard of living to Russia. After all that Marxist
					hype, after all those queues, <i>after all that dying</i>, didn’t they deserve better? The
					Russians we met at the university or in downtown Moscow were as ignorant of our lives
					as we were of theirs, but they were <i>interested,</i> first of all, in material inequalities.
					Political discussion did finally take place once it appeared that we could be trusted.
					At that time, such conversation seldom took a dissident tone. Despite the Stalinist
					excesses that Khrushchev’s 1957 revelations had made public, few Russians with whom
					we spoke could imagine anything but Soviet rule. While believing in elections, our
					friends tended to think that having more than one party would lead to anarchy. As
					far as personal rights were concerned, no one could really understand the concept
					of innocence until guilt is proven. Our acquaintances tended to take the not altogether
					unusual view, even here in the States, that murderers, thieves, and rapists would
					fill the streets unless they were kept behind bars until they had proved their innocence.
					</span></p>
<p class="tx">
					Much of our commerce with Russians involved a Baptist couple that Lois met at their
					church. The situation of Olga and Sergey may help to explain the situation of believers
					under late Soviet rule. Both had “engineering” degrees. “Engineering,” in the Soviet
					Union, might mean extreme academic or industrial technical competence, but it could
					also mean work in food processing as the foreman of a canning line. Since Olga and
					Sergey were practicing Baptists, they could not expect to rise high on the technical
					career <span id="z20-1">ladder. On the other hand, Sergey then wrote for and later became editor of
					the Baptist magazine <i>Brotherly Herald.</i> The four-hour services that we attended on
					Wednesday night or Sunday in the Moscow Baptist church were always crowded. We alone,
					not as inured to physical stress, were enjoined to share the one bench kept for foreign
					visitors. The congregation stood, perhaps eight hundred people. This was the only
					Baptist church in Moscow, but Sergey told me that there were twelve million Baptists
					in Russia at that time. (It may be of interest that while Olga’s mother was a Baptist,
					her father, the manager of a small factory, was a member of the Communist Party.)
					There certainly must have been ten times as many practicing Orthodox believers; for
					there were a number of “working” Orthodox churches in Moscow then, and we visited
					some of them. The services were long, and the congregations, of all ages and genders,
					stood throughout. This was not primarily because of a lack of space, but rather because
					it is a cardinal assumption of Russian Orthodoxy that the body is tried for the soul’s
					sake. Exceptions were made for the very old and the infirm. While the Baptists sang
					hymns that we were used to in the States, the music of Orthodoxy was exotic, medieval,
					full of minors, and grand. The icon screen which mediates between this material world
					and the world of the spirit, the veneration of special images for various ills and
					afflictions, the mixture of rich incense and fat candle-smoke from thin, yellow-brown
					candles all seemed, to us, not only exotic, but strangely moving. Particularly impressive,
					musically, were the chants of an all-night Easter service in the cathedral of the
					Old Believers, a large sect which broke from the state church in the seventeenth
					century. </span></p>
<p class="tx">
					I dwell upon church matters, though an agnostic, because most Americans seem to think
					that Communism was successful in throttling religion. In truth, this proved to be
					beyond the power of even Stalin, though the church saw better and worse times. Many
					believers suffered exile or death, and many church buildings were desecrated or destroyed.
					In 1961, the impossibility of material economic advancement, which did not mean starvation,
					was the price that our Baptist friends paid for their beliefs. For us, this would
					be an extremely heavy price to pay. Olga and Sergey seemed to find it insignificant,
					and they were by no means grim and humorless or lacking in physical needs and desires.
					They were, I guess, simply genuine Christians. </p>
<p class="tx">
					Only once, some time in the 1980s, did Sergey try to evangelize me, though he was
					a preacher, and a powerful one, besides being an editor. At that time, the Russian
					Baptists were allowed to send a delegation to a church congress in St. Louis. Then
					Sergey, having seen the many <i>things</i> Americans live among and with and for, feared
					for my soul. I managed to convince him that success didn’t mean much to me, and he
					ceased his direct efforts. During another visit, I found him in the hospital. Preparing
					hurriedly for a further overseas congress, he neglected a “stomachache” which resulted
					in peritonitis. “As it happens,” he said with a grin, “my life was saved by American
					antibiotics, the first shipment this hospital has been able to obtain. Although I
					haven’t been able to save your soul, you have saved my life.” Sergey then went on
					to inform me that this was the very hospital to which Lenin was brought, in the ‘20s,
					when he was shot by Fanya Kaplan. “Just look,” he <span id="z22-1">grinned, wickedly, “what piety!
					They haven’t even changed the wallpaper, and it’s been sixty years.” It is true that
					even Sergey wouldn’t have joked so in 1961, or even in 1969. </span></p>
<p class="tx">
					When asked a direct question, it is Sergey’s way to take one seriously. Unfailingly,
					he begins with <i>“Seychas, minutochku,”</i> one moment, please. He thinks, then he gives
					you his best possible answer. On the occasion when I found him in the hospital, I
					was vetted first at the Baptist church office. Even in the early ‘80s, Russians didn’t
					always absolutely <i>need</i> foreign visitors. Then, after the obligatory lunch, good cabbage
					soup and black bread and butter, with the church staff, a secretary, Vera Petrovna,
					called a taxi and took me to Sergey’s hospital. On the way we passed the Lubyanka,
					the KGB building, and Vera murmured: “Such an <i>unhappy</i> building.” Later, when Sergey
					mentioned working with the Metropolitan, the head of the Russian Orthodox Church,
					I asked, as I needn’t have, “Do you kiss his ring?” </p>
<p class="tx">
<i>“Seychas, minutochku,”</i> came the traditional reply. “Yes, I do. Whom do <i>you</i> bow to,
					Dik?” </p>
<p class="tx">
					Both Vera’s remark about the “unhappy” KGB headquarters, and she meant <i>elementally</i>
					suffused with misfortune, and Sergey’s response to my question, which implied, of
					course: “Everyone bows. The question is, to whom? “ express what I find special,
					characteristic about Russia, or about some Russians. For now, I’ll just call this
					thing “high seriousness.” It is a characteristic that I deeply admire and need. </p>
<p class="tx">
					During the winter of 1961, Lois had found it possible to visit Zagorsk, then the
					site of the only functioning Russian Orthodox seminary, and to <span id="z23-1">interview the Metropolitan,
					supreme head of the Orthodox church in Russia. To her general questions about the
					state of the church she received bland and optimistic replies. She was given a private
					tour of the seminary’s especially rich collection of icons, altar cloths, and other
					ritual appurtenances. The collection, chronologically arranged, large, valuable and
					full of rarities, was sufficient to suggest that the governments of Lenin and Stalin,
					no matter how ideologically atheistic, had been unable, or had felt it unfeasible,
					to attempt to destroy all vestiges of Russia’s thousand-year history of Orthodoxy.
					</span></p>
<p class="tx">
					For most of our nine months in Moscow, the university, where we lived, could be entered
					only by showing a pass with a photograph. As we compared the life of the streets
					with the university conveniences, living quarters, stores, theaters, and restaurants,
					it seemed likely to us that the passes were required to avoid a sudden sharp rise
					in the population of the university complex. We were, indeed, a much-coddled segment
					of the Moscow population. More than a few of the students managed to smuggle in families
					rather too large for the two-person suites, at least from an American point of view.
					</p>
<p class="tx">
					Nevertheless, sequestration wore upon us. However, long before we were absolutely
					thirsting to quit Moscow, May arrived. Spring was suddenly, warmly present. Life
					seemed to open up. Lois and I trekked to Gorky Street to watch the May Day parade,
					the prime celebration of Soviet pride and power. (Christmas had been marked mainly
					by the appearance of Algerian oranges in the markets, received in return for tanks,
					but still costing a Russian day’s salary apiece.) As the avenue was already teeming
					with early arrivals, we climbed a tall, iron gate and perched on the blunted spears
					at the top to <span id="z24-1">watch the show. After the goose-stepping regiments and the tractor-drawn
					ballistic missiles, the procession became more humane. Factory, farm, and mercantile
					collectives made up by far the largest segment of the three-hour column. Their banners
					bore, for the most part, the all too official “Peace and Friendship” slogan, but
					the people seemed genuinely friendly. The leaders of one collective spotted us on
					our perch, and with hand gestures and friendly cries invited us to join them. In
					this way, two Americans were part of the column reviewed by Khrushchev and Co. in
					Red Square, in front of Lenin’s tomb, on May Day 1962. </span></p>
<p class="tx">
					On the tram going home, a drunken Red Army major refused to produce a ticket, claiming
					that “the Red Army rides free on May Day.” The not hulking uniformed lady conductor
					simply seized him by collar and belt and threw him into a muddy cross street. This
					seemed a surprising but sufficiently democratic conclusion to the holiday that President
					Eisenhower, never a great radical, renamed “Law Day” in the States. </p>
<p class="tx">
					May brought not only a softening of irongray winter, but also of the concierges who
					guarded the university doors. They stopped asking for passes. Their sliding windows
					were often simply empty: they’d found better things to do in the spring weather.
					This made us think that we might consider entertaining outside friends, a forbidden
					pleasure until now. The Iron Curtain seemed to be relaxing with the coming of summer.
					</p>
<p class="tx">
					The American and British embassies greatly cushioned our experience of Soviet reality.
					Their clubs, pubs, and mailing facilities were put at our disposal. A small grocery
					store stocked with Scandinavian imports in the <span id="z25-1">basement of the U.S. Embassy absolutely
					electrified our otherwise Russian bill of fare. We had gone caroling with the two
					embassies’ singers, attended the U.S. ambassador’s New Year’s Eve reception as invited
					guests and were generally looked after. Our group wanted, at least symbolically,
					to return some fraction of this generosity. Sharing the feeling of spring thaw, we
					asked the university for permission to entertain those members of the British and
					American embassy staff who had been most friendly and helpful. We planned a small
					reception and a tour of the premises. </span></p>
<p class="tx">
					As it happened, none of the embassy people had ever been allowed into Moscow State.
					In contrast with our more or less ignored existence in the Soviet capitol, they were
					guarded in their compounds and followed when they traveled or simply went to work,
					and their telephones were tapped. All of our mail that came through the Russian post
					office was opened. (I once had a postal clerk apologize because a letter had not
					been glued shut again.) In any event, the friends, whom we were foolish enough to
					invite before we had university approval, looked forward to crossing at least one
					forbidden boundary: the portals of Moscow State University. </p>
<p class="tx">
					As it turned out, our <i>starosta,</i> “eldest” or group leader, was informed that our request
					was refused. Since we had already invited our friends, we were embarrassed and doubly
					angry. This was our first experience of a serious <i>nyet</i>, sheltered creatures that
					we had been. There was nothing to do but inform our guests of our failure. They were,
					as they had been all year, most understanding. We were enraged. We wanted vengeance.
					</p>
<p class="tx">
					At about the same time that we requested permission for a reception, we <span id="z26-1">presented
					our proposed schedule for the two-week tour of the country. This tour was a part
					of President Eisenhower’s student exchange contract with Secretary Khrushchev. We
					asked, on the advice of the geographer who was one of our group, to visit Central
					Asia (Tashkent, Samarkand and Bukhara), Azerbaijan (Baku), Georgia (Tbilisi), Armenia
					(Erevan and Lake Sevan) and the Ukraine (Kiev). Our wish list was granted, with the
					exception of Bukhara, as Aeroflot had no flights to that city. </span></p>
<p class="tx">
					I assume that the geographer was the one person in the group with anything but the
					crudest notion about even the location of Bukhara. Our initial choices had been limited
					to “open” areas, and much of the Union was closed to foreign travel. (The U.S. State
					Department, quite correctly assuming that one could deal with the Soviet government
					only in terms of a strict <i>quid pro quo,</i> had responded to “closed” areas by superimposing
					a map of the Union on a U.S. map and closing those areas where Soviet closed zones
					fell. Thus, Soviet students could visit Manhattan, but not Brooklyn, Gary, but not
					Chicago.) In any event, Bukhara was an “open” city. Simmering because of our aborted
					party, we announced that we would accept no journey that did not include Bukhara.
					We warned that we would release this decision to the American press. </p>
<p class="tx">
					This decision was, of course, not taken without dissension. A few wiser heads among
					us, not mine, wondered if we wanted to risk our one chance to see the larger Union,
					for this was long before today’s widespread tourism. Still, democrats that we were,
					the stubborn, angry majority ruled and, as it happened, won. Bukhara was included
					in our tour by commandeering the <span id="z27-1">official plane of a local Party bigwig. We traveled
					in the sofas that ringed the lovely Bukhara rug in the middle of the passenger compartment.
					I suppose, now, that the exchange program seemed valuable enough to the Russians
					to put up with our blackmail. After nine months of being agreeable, we suddenly realized
					that we had <i>clout</i>. Still, I don’t really know why the Russian authorities gave in.
					The lesson seemed to be that one could, on occasion, reverse a seemingly concrete
					<i>nyet.</i> </span></p>
<p class="tx">
					The journey itself was memorable because it brought us to just a few of those myriad
					areas in the Union where Russian culture and the Russian language were merely tolerated,
					on sufferance. We knew, abstractly, that <span class="underline" id="AocX">Pravda</span> appeared in more than one hundred
					languages daily, most of them not Russian, or even Slavic. Still, the cerulean blue
					tile mosques of very Muslim Tashkent, the faceted stone churches of Tbilisi which
					preached Christianity five hundred years before Russia’s acceptance of Orthodoxy
					in 988, the citizen of Erevan who pointed out “our” mountain, Ararat, far to the
					west in Turkey now, even the softer speech and manners of Ukrainian Kiev, made the
					Union’s variety real. It was a special pleasure to speak our still very imperfect
					Russian with people to whom the language was also a second language, foreign. They
					made us sound pretty good. </p>
<p class="tx">
					As the proverb has it: “Moscow is far, and God is high above: I’ll do what I like.”
					We found honest and open conversation much easier to manage in these ethnic republics.
					As Andrey Amalrik was to suggest in the book <i>Will the Soviet Union Survive Until
					1984</i>, the conversation turned often upon questions of local sovereignty. An Intourist
					guide in Georgia, showing us <span id="z28-1">with great pride a museum of magnificently enameled,
					silver-mounted icons, assured his audience that Georgians were all atheists now,
					and then then added, “Of course, we were evangelized, civilized, five hundred years
					before those northern barbarians.” Our surprise at the collapse of the Communist
					regime in 1991 may have been nothing compared to the Russian surprise at the disaffection
					of their non-Russian republics. </span></p>
<p class="tx">
					A bright memory from our farewell excursion obtrudes. We were, I remember, in the
					south, in Tbilisi, and were offered an evening outing to an ancient Greek theater.
					There had, in ancient times, been Greek colonies all along the Black Sea coast. </p>
<p class="tx">
					We went as a group in an ancient bus and then walked several hundred yards to the
					crest of a low ridge. Below us lay, in the reddish stone of the region, a perfect,
					and perfectly round, orchestra for the actors. The crescents of stone benches for
					perhaps a thousand viewers rose toward us. The theater seemed all complete in the
					evening light, neither cracks nor gaps showing. It felt, in this eastern land, very
					like our western home. </p>
<p class="tx">
					I remembered then a young couple, met that day in the elevator of our hotel, who
					proudly carried their little son. “What is his name?” </p>
<p class="tx">
					They looked at me and asked, “Are you American?” </p>
<p class="tx">
					I nodded. </p>
<p class="tx">
					“His name,” they said, in 1961, during strict Socialism, “his name is <i>Azad</i>. It means
					‘Freedom.’ It is a very old Armenian name.” And they laughed, winked, and left. </p>
<p class="tx">
					Now, in this old theater, as in the elevator, one felt the fragility of the <span id="z29-1">satrapy,
					indeed, of any fixed political arrangement, and that the world, even here, in this
					corner of the Union, was large and as fluid as the sea. This was not a feeling often
					experienced in the old Union. </span></p>
<p class="tx">
					The thickness of the “Iron Curtain,” in 1961, may be suggested by the following anecdote:
					in Paris, outside Notre Dame, Lois and I bought a foot-high plaster gargoyle, its
					fangs deep in a dog, or a baby. We carried it with us to Russia, not trusting the
					mail home. The cleaning lady whom we saw once a week for nine months asked us, near
					the end of our stay, if that “Satan” on the bookcase was our god. That she was perfectly
					serious indicates the mental, cultural, even chronological gap between the United
					States and Russia in 1961 or 2012 for that matter. I forget how we answered, but
					the question was a poignant one. Lois and I, it was becoming clear, worshipped different
					gods. </p>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// chapter01.vue?cd39559c